import { useEffect, useMemo, useRef } from 'react';
import {
  Flex,
  InputGroup,
  InputLeftElement,
  Input,
  Select,
  Spinner,
} from '@chakra-ui/react';
import { SUPPORTED_CURRENCIES, CURRENCY_SYMBOLS, SupportedCurrency } from 'constants/currency';
import { ExchangeRateMap } from 'api/exchangeRate';

export interface CurrencyInputProps {
  amount: number | undefined;
  currency: string | undefined;
  onAmountChange: (next: number | undefined) => void;
  onCurrencyChange: (next: string) => void;
  supportedCurrencies?: readonly SupportedCurrency[];
  exchangeRates?: ExchangeRateMap;
  isLoadingRates?: boolean;
  convertOnCurrencyChange?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
}

const roundMoney = (value: number): number => Math.round(value * 100) / 100;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const convertAmount = (amount: number, from: string, to: string, rates: ExchangeRateMap): number | undefined => {
  const fromRate = rates[from];
  const toRate = rates[to];
  if (!isFiniteNumber(fromRate) || !isFiniteNumber(toRate) || fromRate <= 0 || toRate <= 0) return undefined;
  const amountInUsd = amount / fromRate;
  const converted = amountInUsd * toRate;
  return roundMoney(converted);
};

export default function CurrencyInput({
  amount,
  currency,
  onAmountChange,
  onCurrencyChange,
  supportedCurrencies = SUPPORTED_CURRENCIES,
  exchangeRates,
  isLoadingRates = false,
  convertOnCurrencyChange = true,
  placeholder = 'Enter Amount',
  isDisabled = false,
}: CurrencyInputProps) {
  const resolvedCurrency = (currency || 'USD').toUpperCase();
  const symbol = useMemo(() => {
    const asSupported = supportedCurrencies.find(c => c === resolvedCurrency) as SupportedCurrency | undefined;
    return (asSupported && CURRENCY_SYMBOLS[asSupported]) || '$';
  }, [resolvedCurrency, supportedCurrencies]);

  const lastCurrencyRef = useRef<string>(resolvedCurrency);

  useEffect(() => {
    lastCurrencyRef.current = resolvedCurrency;
  }, [resolvedCurrency]);

  const handleCurrencyChange = (nextCurrency: string) => {
    const prevCurrency = lastCurrencyRef.current;
    const next = nextCurrency.toUpperCase();

    onCurrencyChange(next);

    if (!convertOnCurrencyChange) return;
    if (!exchangeRates) return;
    if (!isFiniteNumber(amount)) return;
    if (prevCurrency === next) return;

    const converted = convertAmount(amount, prevCurrency, next, exchangeRates);
    if (converted !== undefined) {
      onAmountChange(converted);
    }
  };

  return (
    <Flex gap={2} alignItems="center">
      <InputGroup>
        <InputLeftElement pointerEvents="none" color="gray.500">
          {symbol}
        </InputLeftElement>
        <Input
          fontSize="sm"
          type="number"
          placeholder={placeholder}
          value={amount ?? ''}
          onChange={(e) => {
            const next = e.target.value === '' ? undefined : Number(e.target.value);
            onAmountChange(Number.isFinite(next as number) ? (next as number) : undefined);
          }}
          isDisabled={isDisabled}
        />
      </InputGroup>
      <Select
        width="120px"
        value={resolvedCurrency}
        onChange={(e) => handleCurrencyChange(e.target.value)}
        isDisabled={isDisabled}
      >
        {supportedCurrencies.map(code => (
          <option key={`currency_${code}`} value={code}>
            {code}
          </option>
        ))}
      </Select>
      {isLoadingRates && <Spinner size="sm" />}
    </Flex>
  );
}

